import { BadgeCheck } from "lucide-react";
import type * as React from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import type { ProjectProposalItem } from "@/features/projects/services/projects-api.service";
import { Btn } from "../design-primitives";
import { ProposalDetail, ProposalSummary } from "./proposal-detail";

export type ComparableProposalItem = ProjectProposalItem & {
	proposal: NonNullable<ProjectProposalItem["proposal"]>;
};

interface ProposalCompareDialogProps {
	items: [ComparableProposalItem, ComparableProposalItem] | null;
	busy: boolean;
	onOpenChange: (open: boolean) => void;
	onHire: (item: ComparableProposalItem) => void;
	onDecline: (item: ComparableProposalItem) => void;
}

function CompareColumn({
	item,
	busy,
	onHire,
	onDecline,
}: {
	item: ComparableProposalItem;
	busy: boolean;
	onHire: (item: ComparableProposalItem) => void;
	onDecline: (item: ComparableProposalItem) => void;
}): React.ReactElement {
	const { t } = useTranslation();
	return (
		<section
			aria-label={item.designer.name}
			className="flex flex-col gap-4 rounded-lg border border-dashboard-border p-4"
		>
			<div>
				<div className="flex items-center gap-2">
					<h3 className="text-base font-semibold text-dashboard-text">
						{item.designer.name}
					</h3>
					{item.designer.isVerified && (
						<BadgeCheck className="h-4 w-4 shrink-0 text-dashboard-accent" />
					)}
				</div>
				{item.designer.firmName && (
					<p className="text-sm text-dashboard-muted">
						{item.designer.firmName}
					</p>
				)}
			</div>
			<ProposalSummary proposal={item.proposal} />
			<ProposalDetail proposal={item.proposal} />
			<div className="mt-auto flex gap-3">
				<Btn
					variant="primary"
					size="sm"
					disabled={busy}
					onClick={() => onHire(item)}
				>
					{t("proposalCompare.hire")}
				</Btn>
				<Btn
					variant="ghost"
					size="sm"
					disabled={busy}
					onClick={() => onDecline(item)}
				>
					{t("proposalCompare.decline")}
				</Btn>
			</div>
		</section>
	);
}

/** Two designers' proposals for one project, side by side. */
export function ProposalCompareDialog({
	items,
	busy,
	onOpenChange,
	onHire,
	onDecline,
}: ProposalCompareDialogProps): React.ReactElement {
	const { t } = useTranslation();
	return (
		<Dialog open={items !== null} onOpenChange={onOpenChange}>
			{items && (
				<DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
					<DialogHeader>
						<DialogTitle>{t("proposalCompare.title")}</DialogTitle>
						<DialogDescription>
							{t("proposalCompare.description", {
								first: items[0].designer.name,
								second: items[1].designer.name,
							})}
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 md:grid-cols-2">
						{items.map((item) => (
							<CompareColumn
								key={item.proposal.id}
								item={item}
								busy={busy}
								onHire={onHire}
								onDecline={onDecline}
							/>
						))}
					</div>
				</DialogContent>
			)}
		</Dialog>
	);
}
